import { useState } from 'react'
import { Link } from 'react-router-dom'
import { SEO } from '@/components/seo'
import { Button, Input, Select, Textarea } from '@/components/ui'

const benefits = [
  {
    icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    title: 'Earn Rewards',
    description: 'Get £100-£500 per successful referral, paid as vouchers or cash',
  },
  {
    icon: 'M13 10V3L4 14h7v7l9-11h-7z',
    title: 'Quick & Easy',
    description: 'Simple referral process with dedicated support',
  },
  {
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    title: 'Track Progress',
    description: 'Real-time dashboard to monitor your referrals',
  },
]

export default function PartnerSignupPage() {
  const [track, setTrack] = useState<'physical' | 'cash'>('physical')
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    relationship: '',
    message: '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Form submission would go here
    alert('Thank you for your interest! The Partner Programme is launching soon.')
  }

  return (
    <>
      <SEO
        title="Become a Partner | WealthSpace"
        description="Join the WealthSpace Partner Programme. Refer financial advisers and earn valuable rewards - from vouchers to cash payouts."
        url="/partner-signup"
      />

      <section className="py-20 px-6">
        <div className="max-w-[1100px] mx-auto grid lg:grid-cols-2 gap-16 items-start">
          {/* Left - Info */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-400 text-sm font-medium mb-6">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Partner Programme
            </div>

            <h1 className="font-serif text-[clamp(32px,4vw,44px)] font-normal text-brand-light mb-6 leading-tight">
              Refer advisers,{' '}
              <span className="text-violet-400 italic">earn rewards</span>
            </h1>

            <p className="text-lg text-brand-muted leading-relaxed mb-8">
              Know a financial adviser who could benefit from WealthSpace? Join our Partner
              Programme and earn rewards for every successful referral.
            </p>

            <div className="space-y-6 mb-8">
              {benefits.map((benefit, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-6 h-6 text-violet-400"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d={benefit.icon} />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-brand-light mb-1">{benefit.title}</h3>
                    <p className="text-sm text-brand-muted">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-sm text-brand-subtle">
              Already a partner?{' '}
              <Link to="/partner-dashboard" className="text-violet-400 hover:underline">
                Access your dashboard
              </Link>
            </p>
          </div>

          {/* Right - Form */}
          <div className="p-10 rounded-2xl bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/[0.08]">
            <h2 className="font-serif text-2xl font-medium text-brand-light mb-2">
              Join the Programme
            </h2>
            <p className="text-sm text-brand-muted mb-8">
              Fill out the form below and we'll be in touch.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  label="First name"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="John"
                />
                <Input
                  label="Last name"
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="Smith"
                />
              </div>

              <Input
                label="Email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
              />

              <Input
                label="Phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+44 7700 900000"
              />

              <Input
                label="Company (optional)"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Your company name"
              />

              <Select
                label="Your relationship to advisers"
                required
                value={formData.relationship}
                onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
              >
                <option value="">Select an option</option>
                <option value="adviser">I am an adviser</option>
                <option value="network">Network / Compliance</option>
                <option value="provider">Provider / Platform</option>
                <option value="consultant">Consultant</option>
                <option value="other">Other</option>
              </Select>

              <div>
                <label className="block text-sm font-semibold text-brand-light mb-3">
                  Reward preference
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setTrack('physical')}
                    className={`p-4 rounded-xl text-center transition-all border-2 ${
                      track === 'physical'
                        ? 'bg-violet-500/20 border-violet-500'
                        : 'bg-violet-500/10 border-violet-500/20 hover:border-violet-500/40'
                    }`}
                  >
                    <div className="text-sm font-semibold text-violet-400 mb-1">
                      Vouchers
                    </div>
                    <div className="text-xs text-brand-subtle">Amazon, John Lewis, etc.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTrack('cash')}
                    className={`p-4 rounded-xl text-center transition-all border-2 ${
                      track === 'cash'
                        ? 'bg-amber-500/20 border-amber-500'
                        : 'bg-amber-500/10 border-amber-500/20 hover:border-amber-500/40'
                    }`}
                  >
                    <div className="text-sm font-semibold text-amber-400 mb-1">Cash</div>
                    <div className="text-xs text-brand-subtle">Bank transfer</div>
                  </button>
                </div>
              </div>

              <Textarea
                label="Anything else? (optional)"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Tell us about your network or any questions..."
                rows={3}
              />

              <Button type="submit" className="w-full justify-center">
                Submit Application
              </Button>

              <p className="text-xs text-brand-subtle text-center">
                By submitting, you agree to our{' '}
                <Link to="/terms" className="underline hover:text-brand-muted">
                  Terms
                </Link>{' '}
                and{' '}
                <Link to="/privacy-policy" className="underline hover:text-brand-muted">
                  Privacy Policy
                </Link>
              </p>
            </form>
          </div>
        </div>
      </section>
    </>
  )
}
